import { motion } from "framer-motion";
import { Database, Eye, ShieldAlert, KeyRound, RefreshCw, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";

const SECTIONS = [
  {
    icon: Database,
    title: "Data We Collect",
    text: "To operate correctly, Areon collects and stores minimal necessary Discord data, including: your Discord User ID, username, avatar ID, server IDs (guilds you join or manage), server roles, channel IDs, configuration settings (e.g. custom welcomes, giveaway data, suggestion states), and temporary support ticket transcripts if ticket logging is enabled by your server administrators.",
  },
  {
    icon: KeyRound,
    title: "How We Use Your Data",
    text: "We access Discord data exclusively to perform bot automation — creating support channels, verifying role access in dashboard controls, executing moderator commands, managing reaction roles, and running giveaways or polls. We do not sell, trade, or distribute your private Discord configurations or data to third-party advertisers.",
  },
  {
    icon: Eye,
    title: "AI Processing & Transcripts",
    text: "If a server administrator enables premium ticket assistance (AI Ticket Pilot), support messages sent in ticket channels are temporarily processed via secure APIs to summarise and answer queries. None of this data is used to train foundation models or retained permanently.",
  },
  {
    icon: ShieldAlert,
    title: "Data Deletion & Retention",
    text: "We keep your configuration data only as long as Areon remains in your server. If you kick or ban the bot, all configuration data related to your guild is eligible for permanent cleanup. You can request deletion of all stored data at any time by contacting our support team via Discord.",
  },
  {
    icon: RefreshCw,
    title: "Updates to This Policy",
    text: "We may update this Privacy Policy periodically to reflect service enhancements. Continuing to use the bot or web panel after updates are posted indicates your consent to the updated policy.",
  },
];

export function Privacy() {
  return (
    <div className="mx-auto max-w-3xl py-8 sm:py-12">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-border/50 bg-gradient-to-br from-primary/10 via-card to-card p-6 sm:p-8 mb-8"
      >
        <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-medium text-primary mb-3">
            <ShieldCheck className="h-3 w-3" />
            Privacy Policy
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">
            Your data, protected
          </h1>
          <p className="mt-2 text-[13px] sm:text-sm text-muted-foreground max-w-xl leading-relaxed">
            We respect your privacy and are committed to protecting the data of every server and user.
          </p>
          <p className="mt-4 text-[11px] text-muted-foreground/60 font-mono">Last updated: June 23, 2026</p>
        </div>
      </motion.div>

      {/* Intro */}
      <p className="text-sm text-muted-foreground leading-relaxed mb-6 px-1">
        Areon is built with privacy in mind. This policy details what information we collect, how it is used, and the control you have over it.
      </p>

      {/* Sections */}
      <div className="space-y-3">
        {SECTIONS.map((section, i) => {
          const Icon = section.icon;
          return (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
            >
              <Card className="flex gap-4 p-5 border-border/50 bg-card/50 hover:border-primary/20 transition-colors">
                <div className="h-9 w-9 rounded-lg bg-primary/10 ring-1 ring-primary/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-1">{section.title}</h3>
                  <p className="text-[13px] text-muted-foreground leading-relaxed">{section.text}</p>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Footer */}
      <p className="mt-8 text-center text-xs text-muted-foreground/60">
        Questions about your data?{" "}
        <a href="https://discord.gg/8eh7KrMPXg" target="_blank" rel="noreferrer" className="text-primary hover:underline">
          Reach out on Discord
        </a>.
      </p>
    </div>
  );
}
